import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Search as SearchIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import { adminApi, Organization, OrgSubscriptionTag } from '../services/api';
import OrganizationDialog from '../components/OrganizationDialog';
import { REFERRAL_SOURCES } from '../config/referral-sources';
import { IS_BROKER_ADMIN } from '../config/admin-build';
import {
  BILLING_CELL_SELF_SERVE,
  BILLING_CELL_UNTAGGED,
  billingCellValue,
  effectiveBillingTag,
  renderBillingTagChip,
} from '../components/BillingTagChip';

// Re-exported so existing importers (and the page's own tests) keep their import
// path. The DEFINITIONS moved to BillingTagChip.tsx in #377 so Usage Health can
// call the identical derivation instead of re-rolling / skipping it.
export {
  BILLING_CELL_SELF_SERVE,
  BILLING_CELL_UNTAGGED,
  billingCellValue,
  effectiveBillingTag,
};

// Billing tag chips. broker-AI reads the backend-derived subscriptionTag (live
// self-serve payment state — org-subscriptions join, Marcus 2026-06-29); the
// sales-led IA build reads the manually-set manualBillingStatus (no self-serve
// subscription to auto-derive from — blank until an admin tags the org).
// The shared paying-status union (api.ts) — kept as a local alias so the filter
// helpers read naturally; NOT an independent definition, so it can't drift.
type BillingTag = OrgSubscriptionTag;

// broker-AI filter set includes 'managed'; IA has only the three manual tags.
const BILLING_TAG_FILTERS: { value: BillingTag; label: string }[] = [
  { value: 'paid', label: 'Paid' },
  { value: 'trial', label: 'Trial' },
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'managed', label: 'Managed' },
];
// IA has the three manual tags — the broker set minus 'managed' (comp'd orgs
// are handled by the managed-subscription mechanism, not the manual tag). Derived
// so a new tag added to BILLING_TAG_FILTERS is automatically considered here.
const IA_BILLING_TAG_FILTERS: { value: BillingTag; label: string }[] =
  BILLING_TAG_FILTERS.filter((f) => f.value !== 'managed');

// The Billing-tag chip AND its effective-tag derivation are the shared
// `renderBillingTagChip` / `effectiveBillingTag` / `billingCellValue`
// (src/components/BillingTagChip.tsx) — also used by the Usage Health "Billing"
// column + "Paying only" filter (Trello #335, #377), so the styling, the tag
// vocabulary, and the EFT-payer definition can't drift between the two tables.

// Label + filter sentinel for organisations with no assigned account manager.
const UNASSIGNED_ACCOUNT_MANAGER = 'Unassigned';

// Distinct slice colours for the orgs-per-account-manager pie, cycled by index
// (COLORS[i % len]). The team has a handful of account managers, so 8 is ample;
// the "Unassigned" slice is rendered grey regardless of position.
const ACCOUNT_MANAGER_COLORS = [
  '#3182ce', '#38a169', '#dd6b20', '#805ad5',
  '#d53f8c', '#319795', '#e53e3e', '#2c5282',
];

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [billingFilter, setBillingFilter] = useState('');
  const [accountManagerFilter, setAccountManagerFilter] = useState('');

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listOrganizations();
      
      if (response.success) {
        setOrganizations(response.data.organizations);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleRowClick = (params: GridRowParams) => {
    // Use organization ID (organizationId from backend, stored as 'id')
    navigate(`/organizations/${params.row.id || params.row.name}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Organization ID',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {params.value || params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: 'Display Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'maxRunsPerMonth',
      headerName: 'Monthly Limit',
      width: 150,
      valueGetter: (value: number | null) => value || 50,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={`${params.value} runs`}
          size="small"
          color={params.value >= 200 ? 'success' : 'default'}
          variant="outlined"
        />
      ),
    },
    // Billing status — shown on BOTH builds, but the source differs:
    // - broker-AI: the EFT-aware effectiveBillingTag(). Live self-serve payment
    //   state (the org-subscriptions `subscriptionTag` join: 'managed'|'paid'|
    //   'trial'|'unpaid', Marcus 2026-06-29), EXCEPT an EFT payer shows 'paid'
    //   (it has no Stitch sub so the join reads 'unpaid', but it IS paying —
    //   matches the org's Subscription & Credits panel, Trello #332). Falls back
    //   to the billingMode display when absent.
    // - IA: sales-led, no self-serve subscription — an admin manually tags the
    //   org via the detail-page edit dialog; the column reads `manualBillingStatus`
    //   and shows blank (—) until set.
    {
      field: IS_BROKER_ADMIN ? 'subscriptionTag' : 'manualBillingStatus',
      headerName: 'Billing',
      width: 120,
      // The grid's own column menu (Filter / Sort) reads this value, NOT the
      // rendered chip — so it returns the SAME text the chip shows, keeping the
      // column filter, the Billing dropdown and the chip in agreement (#332).
      valueGetter: (_value, row) => billingCellValue(row as Organization),
      renderCell: (params: GridRenderCellParams) => {
        if (IS_BROKER_ADMIN) {
          // EFT-aware effective tag — the SAME helper the Billing filter uses,
          // so the chip and the filter agree. An EFT payer shows Paid; a managed
          // org with no subscription tag shows Managed (#332).
          const tag = effectiveBillingTag(params.row as Organization);
          if (!tag) {
            // No subscription tag, not managed, not an EFT payer — older backend
            // / self-serve default.
            return (
              <Chip
                label={BILLING_CELL_SELF_SERVE}
                size="small"
                color="default"
                variant="outlined"
              />
            );
          }
          return renderBillingTagChip(tag);
        }
        // IA: manual tag, blank until an admin sets it.
        const manual = params.row.manualBillingStatus as BillingTag | undefined;
        return manual ? (
          renderBillingTagChip(manual)
        ) : (
          <Typography variant="body2" color="text.secondary">
            {BILLING_CELL_UNTAGGED}
          </Typography>
        );
      },
    },
    {
      field: 'accountManagerName',
      headerName: 'Account Manager',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', py: 1 }}>
          {params.row.accountManagerName || params.row.accountManagerEmail ? (
            <>
              {params.row.accountManagerName && (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {params.row.accountManagerName}
                </Typography>
              )}
              {params.row.accountManagerEmail && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {params.row.accountManagerEmail}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </Box>
      ),
    },
    // Referral source — broker-AI distribution-partner tracking, shown only
    // on the broker-AI admin build. (Trello #260)
    ...(IS_BROKER_ADMIN
      ? [
          {
            field: 'referralSource',
            headerName: 'Source',
            width: 130,
            renderCell: (params: GridRenderCellParams) =>
              params.row.referralSource ? (
                <Chip label={params.row.referralSource} size="small" color="info" variant="outlined" />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              ),
          } as GridColDef,
        ]
      : []),
  ];

  const handleSuccess = async () => {
    await fetchOrganizations();
  };

  // Account managers present in the loaded orgs, for the filter dropdown. Sorted
  // alphabetically; an "Unassigned" option is appended when any org has no
  // account manager set. Shown on both builds (the AM column is shared).
  const accountManagerOptions = useMemo(() => {
    const names = new Set<string>();
    let hasUnassigned = false;
    for (const org of organizations) {
      if (org.accountManagerName) names.add(org.accountManagerName);
      else hasUnassigned = true;
    }
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b));
    return hasUnassigned ? [...sorted, UNASSIGNED_ACCOUNT_MANAGER] : sorted;
  }, [organizations]);

  // Total organisations per account manager, for the breakdown pie. Reflects the
  // FULL org list (not the filtered subset) — "total orgs per owner". Orgs with
  // no account manager roll into an "Unassigned" slice. Sorted by count desc.
  const orgsPerAccountManager = useMemo(() => {
    const counts = new Map<string, number>();
    for (const org of organizations) {
      const key = org.accountManagerName || UNASSIGNED_ACCOUNT_MANAGER;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [organizations]);

  // Client-side filter mirroring the Users tab search — orgs are loaded in
  // full by listOrganizations(), so filtering happens in-memory.
  const filteredOrganizations = organizations.filter((org) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      org.id?.toLowerCase().includes(term) ||
      org.name?.toLowerCase().includes(term) ||
      org.accountManagerName?.toLowerCase().includes(term) ||
      org.accountManagerEmail?.toLowerCase().includes(term) ||
      // Only match the referral source where the Source column is shown
      // (broker-AI admin) — don't let the IA build search a hidden field.
      (IS_BROKER_ADMIN && (org.referralSource?.toLowerCase().includes(term) ?? false));
    // Broker admins never set the filter (it's hidden), so sourceFilter stays ''.
    const matchesSource = !sourceFilter || org.referralSource === sourceFilter;
    // Billing-state filter — effectiveBillingTag() reads subscriptionTag on
    // broker-AI, manualBillingStatus on IA (both builds show the filter now).
    const matchesBilling = !billingFilter || effectiveBillingTag(org) === billingFilter;
    // Account-manager filter — an explicit "Unassigned" selection matches orgs
    // with no account manager; otherwise match the exact manager name.
    const matchesAccountManager =
      !accountManagerFilter ||
      (accountManagerFilter === UNASSIGNED_ACCOUNT_MANAGER
        ? !org.accountManagerName
        : org.accountManagerName === accountManagerFilter);
    return matchesSearch && matchesSource && matchesBilling && matchesAccountManager;
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Organizations
          </Typography>
          <Chip label={`${organizations.length} organizations`} color="primary" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Create Organization
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          {/* Referral-source filter — shown only on the broker-AI admin build.
              (Trello #260) */}
          {IS_BROKER_ADMIN && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="source-filter-label">Source</InputLabel>
              <Select
                labelId="source-filter-label"
                label="Source"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(String(e.target.value))}
              >
                <MenuItem value="">
                  <em>All sources</em>
                </MenuItem>
                {REFERRAL_SOURCES.map((src) => (
                  <MenuItem key={src} value={src}>
                    {src}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* Billing-state filter — shown on BOTH builds. broker-AI: Paid / Trial
              / Unpaid / Managed (auto-derived subscriptionTag). IA: Paid / Trial /
              Unpaid (the manual tag). Lets the team isolate paying vs unpaid orgs. */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="billing-filter-label">Billing</InputLabel>
            <Select
              labelId="billing-filter-label"
              label="Billing"
              value={billingFilter}
              onChange={(e) => setBillingFilter(String(e.target.value))}
            >
              <MenuItem value="">
                <em>All billing</em>
              </MenuItem>
              {(IS_BROKER_ADMIN ? BILLING_TAG_FILTERS : IA_BILLING_TAG_FILTERS).map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Account-manager filter — shown on BOTH builds (the Account Manager
              column is shared). Options are the account managers present in the
              loaded orgs + an "Unassigned" bucket for orgs with none set. */}
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="account-manager-filter-label">Account Manager</InputLabel>
            <Select
              labelId="account-manager-filter-label"
              label="Account Manager"
              value={accountManagerFilter}
              onChange={(e) => setAccountManagerFilter(String(e.target.value))}
            >
              <MenuItem value="">
                <em>All account managers</em>
              </MenuItem>
              {accountManagerOptions.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <DataGrid
          rows={filteredOrganizations}
          columns={columns}
          getRowId={(row) => row.id || row.name}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          onRowClick={handleRowClick}
          autoHeight
          sx={{
            border: 'none',
            cursor: 'pointer',
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'action.hover',
            },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          }}
        />
      </Card>

      {/* Breakdown of total orgs per account manager. Reflects the full org
          list (independent of the filters above); orgs with no manager are an
          "Unassigned" slice. Shown on both builds — the AM column is shared. */}
      {!loading && orgsPerAccountManager.length > 0 && (
        <Card sx={{ mt: 3, p: 2 }} data-testid="orgs-per-account-manager">
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Organisations per account manager
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Breakdown of all {organizations.length} organisations by account manager (independent of the filters above).
          </Typography>
          <Box sx={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={orgsPerAccountManager}
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {orgsPerAccountManager.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={
                        entry.name === UNASSIGNED_ACCOUNT_MANAGER
                          ? '#a0aec0'
                          : ACCOUNT_MANAGER_COLORS[index % ACCOUNT_MANAGER_COLORS.length]
                      }
                    />
                  ))}
                </Pie>
                <RechartsTooltip formatter={(value: number, name: string) => [`${value} orgs`, name]} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Card>
      )}

      <OrganizationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default OrganizationsPage;
